<template>
  <div class="flex w-80 items-center space-x-2">
    <icon-base
      v-if="model.loading"
      :icon="IconLoadingCircle"
      class="h-5 w-5 animate-spin rounded-full text-primary"
      view-box="0 0 20 20"
      data-cy="document-loading-spinner"
    />
    <div v-else-if="model.failed" class="flex items-center space-x-2">
      <button @click="retryFailedUpload(model.id)">
        <icon-base :icon="IconRestart" />
      </button>
      <icon-base :icon="IconFail" width="16" height="16" class="text-red-500" />
    </div>
    <icon-base
      v-else-if="model.file_type === FILE_TYPE_OTHER"
      :icon="IconWarning"
      class="text-california-500"
    />
    <icon-base v-else height="25" width="25" :icon="icon" />
    <div>
      <button
        data-cy="document-name-btn"
        class="block break-all text-left hyphens-auto hover:no-underline cursor-default"
        :class="{ 'hover:underline hover:cursor-pointer': enablePreview }"
        @click="enablePreview ? showFilePreview() : undefined"
      >
        {{ model.original_name }}
      </button>
      <div v-if="description" class="text-gray-400">
        {{ description }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { storeToRefs } from "pinia";

import { FILE_TYPE_OTHER } from "@/helpers/constants/deals";
import { getExtensionFromName } from "@/helpers/files";
import { formatDateCustom } from "@/helpers/formatting";
import { useDocumentsState } from "@/stores/documents";

import IconFail from "@/components/icons/IconFail.vue";
import IconFile from "@/components/icons/IconFile.vue";
import IconLoadingCircle from "@/components/icons/IconLoadingCircle.vue";
import IconRestart from "@/components/icons/IconRestart.vue";
import IconWarning from "@/components/icons/IconWarning.vue";
import IconImgSmall from "@/components/icons/fileTypes/IconImgSmall.vue";
import IconPdfSmall from "@/components/icons/fileTypes/IconPdfSmall.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { DocumentTableItem } from "@/models/common";

const {
  model,
  enablePreview = true,
  useAsDescription = "date",
} = defineProps<{
  model: DocumentTableItem;
  enablePreview?: boolean;
  useAsDescription?: "date" | "file-type";
}>();

const { t } = useI18n();
const documentsState = useDocumentsState();
const { documentIsUploaded, retryFailedUpload } = documentsState;
const { previewDealDocumentFile } = storeToRefs(documentsState);

const icon = computed(() => {
  if (model.mime_type?.includes("image")) {
    return IconImgSmall;
  }

  if (!model.original_name) {
    return IconFile;
  }

  const fileExtension = getExtensionFromName(model.original_name);

  switch (fileExtension?.toLowerCase()) {
    case "pdf":
      return IconPdfSmall;
    default:
      return IconFile;
  }
});

const description = computed(() => {
  if (!documentIsUploaded(model)) {
    return "";
  }

  if (useAsDescription === "date" && model.created_at) {
    return formatDateCustom(model.created_at, "MMMM dd, yyyy, hh:mm aa", true);
  }

  if (useAsDescription === "file-type" && model.file_type) {
    return t(`FILE_TYPES.${model.file_type}`);
  }

  return "";
});

const showFilePreview = () => {
  if (!documentIsUploaded(model)) {
    return;
  }
  previewDealDocumentFile.value = model;
};
</script>
